import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  InputAdornment,
  FormControlLabel,
  CircularProgress,
  Pagination,
} from '@mui/material';
import {
  Edit3,
  Download,
  Columns,
  Search,
  Building2,
  TrendingUp,
  TrendingDown,
  MoreVertical,
  Plus,
  Upload,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';
import EditBuildingDialog from './EditBuildingDialog';
import AddUnitsDialog from './AddUnitsDialog';
import MarketDataCollection from './MarketDataCollection';

const BuildingsGrid = ({
  onAddUnits,
  refreshTrigger,
  onAddBuilding,
  onImport,
  onDownloadTemplate,
  onBuildingClick,
}) => {
  const { getAccessToken } = useAuth();
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState({});
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [addingUnitsBuilding, setAddingUnitsBuilding] = useState(null);
  const [dismissedPendingIds, setDismissedPendingIds] = useState([]);

  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    address: true,
    totalUnits: true,
    occupied: true,
    vacant: true,
    notice: true,
    totalCurrentRent: true,
    totalMarketRent: true,
    totalOpportunity: true,
    lastUpdate: false,
    notes: false,
  });

  // Fetch buildings data
  const fetchBuildings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = getAccessToken();
      const url = new URL(
        `${process.env.REACT_APP_NODE_URL}/buildings/buildings`,
      );
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());
      if (searchQuery) {
        url.searchParams.append('search', searchQuery);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage =
          data.message || `HTTP error! status: ${response.status}`;
        if (response.status === 404) {
          setError('noBuildings');
          setBuildings([]);
          setTotal(0);
          setTotalPages(0);
          return;
        }
        throw new Error(errorMessage);
      }

      if (data.status) {
        setBuildings(data.data || []);
        setTotal(data.pagination?.total || 0);
        setTotalPages(data.pagination?.totalPages || 0);
      } else {
        const errorMessage = data.message || 'Failed to fetch buildings';
        if (
          errorMessage.includes('No buildings found') ||
          errorMessage.includes('no buildings')
        ) {
          setError('noBuildings');
          setBuildings([]);
          setTotal(0);
          setTotalPages(0);
          return;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching buildings:', err);
      if (err.message && err.message.includes('No buildings found')) {
        setError('noBuildings');
      } else {
        setError(err.message || 'Failed to fetch buildings');
      }
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, getAccessToken]);

  // Debounce search and fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to first page on search
      // fetchBuildings will be triggered by the page change
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch on mount and page change
  useEffect(() => {
    fetchBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Refresh when refreshTrigger changes (e.g., after adding a building)
  useEffect(() => {
    if (refreshTrigger) {
      setPage(1); // Reset to first page
      fetchBuildings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleSelectAll = () => {
    if (selectedIds.length === buildings.length && buildings.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(buildings.map(b => b.id));
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    setSelectedIds([]); // Clear selection on page change
  };

  const handleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  };

  const handleEdit = building => {
    setEditingBuilding(building);
  };

  const handleAddUnits = building => {
    setAddingUnitsBuilding(building);
  };

  const handleDialogClose = () => {
    setEditingBuilding(null);
    setAddingUnitsBuilding(null);
  };

  const handleDialogSuccess = () => {
    fetchBuildings();
    handleDialogClose();
  };

  const calculateTotalCurrentRent = building => building.totalCurrentRent || 0;
  const calculateTotalMarketRent = building => building.totalMarketRent || 0;
  const calculateTotalOpportunity = building =>
    Math.max(
      0,
      (building.totalMarketRent || 0) - (building.totalCurrentRent || 0),
    );

  // No need for client-side filtering since we're doing server-side search
  // Show all buildings in the table (including pending)
  const pendingBuildings = buildings.filter(
    building =>
      building.status === 'pending' &&
      !dismissedPendingIds.includes(building.id),
  );

  const handleDismissPending = id => {
    setDismissedPendingIds(prev => [...prev, id]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#F9F9F9',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          paddingX: { xs: '1rem', md: '1.5rem' },
          paddingY: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              maxWidth: { xs: '100%', sm: '448px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <TextField
              placeholder='Search buildings...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size='small'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Search size={16} color='#6B7280' />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant='contained'
              size='small'
              startIcon={<Plus size={16} />}
              onClick={onAddBuilding}
              sx={{
                backgroundColor: '#39585C',
                textTransform: 'none',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                '&:hover': { backgroundColor: '#2A3F42' },
              }}
            >
              Add Building
            </Button>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Upload size={16} />}
              onClick={onImport}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Import from CSV/Excel
            </Button>
            <Button
              variant='text'
              size='small'
              onClick={async () => {
                try {
                  const accessToken = getAccessToken();
                  const response = await fetch(
                    `${process.env.REACT_APP_NODE_URL}/buildings/buildings/template`,
                    {
                      method: 'GET',
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                      },
                    },
                  );

                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'buildings-import-template.csv';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  }
                } catch (error) {
                  console.error('Error downloading template:', error);
                }
              }}
              sx={{
                textTransform: 'none',
                color: '#6B7280',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                '&:hover': { color: '#39585C' },
              }}
            >
              Download CSV Template
            </Button>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Columns size={16} />}
              onClick={e => setColumnsMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={Boolean(columnsMenuAnchor)}
              onClose={() => setColumnsMenuAnchor(null)}
            >
              {Object.entries(visibleColumns).map(([key, value]) => (
                <MenuItem key={key} onClick={() => {}}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={e =>
                          setVisibleColumns(prev => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        size='small'
                      />
                    }
                    label={
                      key.charAt(0).toUpperCase() +
                      key.slice(1).replace(/([A-Z])/g, ' $1')
                    }
                    sx={{ margin: 0 }}
                  />
                </MenuItem>
              ))}
            </Menu>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Download size={16} />}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                fontSize: '0.875rem',
              }}
            >
              Export
            </Button>
          </Box>
        </Box>
      </Box>

      <MarketDataCollection
        pendingBuildings={pendingBuildings}
        onDismiss={handleDismissPending}
      />

      {selectedIds.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#E8F0F1',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '0.75rem', sm: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 500,
                color: '#111827',
              }}
            >
              {selectedIds.length} buildings selected
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Button
                size='small'
                variant='outlined'
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Export Selected
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && buildings.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem',
            }}
          >
            {error === 'noBuildings' ? (
              <>
                <Building2 size={48} color='#6B7280' />
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#111827',
                    textAlign: 'center',
                  }}
                >
                  No Buildings Found
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    textAlign: 'center',
                    maxWidth: '400px',
                  }}
                >
                  {searchQuery
                    ? 'No buildings match your search. Try a different search term.'
                    : "You haven't added any buildings yet. Add your first building to get started."}
                </Typography>
              </>
            ) : (
              <>
                <Typography color='error'>{error}</Typography>
                <Button variant='outlined' onClick={fetchBuildings}>
                  Retry
                </Button>
              </>
            )}
          </Box>
        ) : buildings.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem',
            }}
          >
            <Building2 size={48} color='#6B7280' />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                textAlign: 'center',
              }}
            >
              No Buildings Found
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                textAlign: 'center',
                maxWidth: '400px',
              }}
            >
              {searchQuery
                ? 'No buildings match your search. Try a different search term.'
                : "You haven't added any buildings yet. Add your first building to get started."}
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'transparent',
              overflowX: 'auto',
            }}
          >
            <Table stickyHeader sx={{ minWidth: { xs: '800px', md: 'auto' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F3F4F6' }}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={
                        buildings.length > 0 &&
                        selectedIds.length === buildings.length
                      }
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < buildings.length
                      }
                      onChange={handleSelectAll}
                      size='small'
                    />
                  </TableCell>
                  {visibleColumns.name && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Building Name
                    </TableCell>
                  )}
                  {visibleColumns.address && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Address
                    </TableCell>
                  )}
                  {visibleColumns.totalUnits && (
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Total Units
                    </TableCell>
                  )}
                  {visibleColumns.occupied && (
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Occupied
                    </TableCell>
                  )}
                  {visibleColumns.vacant && (
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Vacant
                    </TableCell>
                  )}
                  {visibleColumns.notice && (
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Notice
                    </TableCell>
                  )}
                  {visibleColumns.totalCurrentRent && (
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Total Current Rent
                    </TableCell>
                  )}
                  {visibleColumns.totalMarketRent && (
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Total Market Rent
                    </TableCell>
                  )}
                  {visibleColumns.totalOpportunity && (
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Total Opportunity
                    </TableCell>
                  )}
                  {visibleColumns.lastUpdate && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Last Update
                    </TableCell>
                  )}
                  {visibleColumns.notes && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Notes
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      fontSize: '0.875rem',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {buildings.map(building => (
                  <TableRow
                    key={building.id}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell padding='checkbox'>
                      <Checkbox
                        checked={selectedIds.includes(building.id)}
                        onChange={() => handleSelect(building.id)}
                        size='small'
                      />
                    </TableCell>
                    {visibleColumns.name && (
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <Box
                            sx={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '0.5rem',
                              backgroundColor: '#E8F0F1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Building2 size={16} color='#39585C' />
                          </Box>
                          <Typography
                            onClick={() => {
                              if (onBuildingClick) {
                                onBuildingClick(building.name);
                              }
                            }}
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.875rem',
                              cursor: onBuildingClick ? 'pointer' : 'default',
                              '&:hover': onBuildingClick
                                ? {
                                    color: '#39585C',
                                    textDecoration: 'underline',
                                  }
                                : {},
                            }}
                          >
                            {building.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.address && (
                      <TableCell
                        sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                      >
                        {building.address}
                      </TableCell>
                    )}
                    {visibleColumns.totalUnits && (
                      <TableCell align='center'>
                        <Chip
                          label={building.totalUnits}
                          size='small'
                          sx={{
                            fontWeight: 600,
                            borderColor: '#D1D5DB',
                            color: '#374151',
                          }}
                          variant='outlined'
                        />
                      </TableCell>
                    )}
                    {visibleColumns.occupied && (
                      <TableCell align='center'>
                        <Chip
                          label={building.occupied}
                          size='small'
                          sx={{
                            backgroundColor: '#E8F0F1',
                            color: '#39585C',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.vacant && (
                      <TableCell align='center'>
                        <Chip
                          label={building.vacant}
                          size='small'
                          sx={{
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.notice && (
                      <TableCell align='center'>
                        <Chip
                          label={building.notice}
                          size='small'
                          sx={{
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    )}
                    {visibleColumns.totalCurrentRent && (
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 500,
                          color: '#111827',
                          fontSize: '0.875rem',
                        }}
                      >
                        ${calculateTotalCurrentRent(building).toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.totalMarketRent && (
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 500,
                          color: '#111827',
                          fontSize: '0.875rem',
                        }}
                      >
                        ${calculateTotalMarketRent(building).toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.totalOpportunity &&
                      (() => {
                        const totalOpportunity =
                          calculateTotalOpportunity(building);
                        const isPositive = totalOpportunity > 0;
                        const isNegative = totalOpportunity < 0;
                        const iconColor = isPositive
                          ? '#4FA490'
                          : isNegative
                          ? '#EF4444'
                          : '#6B7280';
                        const textColor = isPositive
                          ? '#4FA490'
                          : isNegative
                          ? '#EF4444'
                          : '#6B7280';

                        return (
                          <TableCell align='right'>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: '0.375rem',
                              }}
                            >
                              {isPositive && (
                                <TrendingUp size={16} color={iconColor} />
                              )}
                              {isNegative && (
                                <TrendingDown size={16} color={iconColor} />
                              )}
                              {!isPositive && !isNegative && (
                                <TrendingUp
                                  size={16}
                                  color={iconColor}
                                  style={{ opacity: 0.5 }}
                                />
                              )}
                              <Typography
                                sx={{
                                  color: textColor,
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {isPositive ? '+' : ''}$
                                {Math.abs(totalOpportunity).toLocaleString()}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })()}
                    {visibleColumns.lastUpdate && (
                      <TableCell
                        sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                      >
                        {building.lastUpdate}
                      </TableCell>
                    )}
                    {visibleColumns.notes && (
                      <TableCell
                        sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                      >
                        {building.notes || '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        size='small'
                        onClick={e =>
                          setActionMenuAnchor({
                            ...actionMenuAnchor,
                            [building.id]: e.currentTarget,
                          })
                        }
                        sx={{ padding: '0.25rem' }}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                      <Menu
                        anchorEl={actionMenuAnchor[building.id]}
                        open={Boolean(actionMenuAnchor[building.id])}
                        onClose={() =>
                          setActionMenuAnchor({
                            ...actionMenuAnchor,
                            [building.id]: null,
                          })
                        }
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleEdit(building);
                            setActionMenuAnchor({
                              ...actionMenuAnchor,
                              [building.id]: null,
                            });
                          }}
                          sx={{ fontSize: '0.875rem' }}
                        >
                          <Edit3 size={16} style={{ marginRight: '0.5rem' }} />
                          Edit Building
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleAddUnits(building);
                            setActionMenuAnchor({
                              ...actionMenuAnchor,
                              [building.id]: null,
                            });
                          }}
                          sx={{ fontSize: '0.875rem' }}
                        >
                          <Plus size={16} style={{ marginRight: '0.5rem' }} />
                          Add Units
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          paddingX: { xs: '1rem', md: '1.5rem' },
          paddingY: '0.75rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '0.75rem', sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
            }}
          >
            {total} buildings total
            {searchQuery && ` (filtered)`}
          </Typography>
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Page {page} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size='small'
                shape='rounded'
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <EditBuildingDialog
        open={Boolean(editingBuilding)}
        onClose={handleDialogClose}
        building={editingBuilding}
        onSuccess={handleDialogSuccess}
      />

      <AddUnitsDialog
        open={Boolean(addingUnitsBuilding)}
        onClose={handleDialogClose}
        building={addingUnitsBuilding}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default BuildingsGrid;
